import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { PERMISSIONS, type Permission, ROLE_LABELS } from '@musleempay/shared';
import { Button, useToast } from '@musleempay/ui';
import { useAuth } from '../../hooks/useAuth.js';
import { errorMessage } from '../../lib/api-client.js';
import { STOREFRONT_ROOT_DOMAIN, getStorefrontUrl } from '../../lib/hostname.js';
import { PlanBanner } from './PlanBanner.js';
import { SupportAccessBanner } from './SupportAccessBanner.js';

/**
 * Dashboard shell: sidebar, top bar, and the routed page.
 *
 * On small screens the sidebar becomes an overlay drawer, since the dashboard
 * is genuinely used from phones — merchants check orders away from a desk.
 */

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: JSX.Element;
  /** Hidden when the user lacks this permission. */
  readonly permission?: Permission;
}

/** Small inline icons, so the shell needs no icon dependency. */
function Icon({ path }: { path: string }): JSX.Element {
  return (
    <svg
      className="h-5 w-5 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.6}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/dashboard',
    label: 'Overview',
    icon: <Icon path="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />,
  },
  {
    to: '/orders',
    label: 'Orders',
    permission: PERMISSIONS.ORDERS_VIEW,
    icon: <Icon path="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12A2.25 2.25 0 0118.632 21H5.367a2.25 2.25 0 01-2.237-2.493l1.264-12A2.25 2.25 0 016.633 4.5h10.734a2.25 2.25 0 012.239 2.007z" />,
  },
  {
    to: '/products',
    label: 'Products',
    permission: PERMISSIONS.PRODUCTS_VIEW,
    icon: <Icon path="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />,
  },
  {
    to: '/sales-pages',
    label: 'Sales pages',
    permission: PERMISSIONS.SALES_PAGES_VIEW,
    icon: <Icon path="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />,
  },
  {
    to: '/customers',
    label: 'Customers',
    permission: PERMISSIONS.CUSTOMERS_VIEW,
    icon: <Icon path="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />,
  },
  {
    to: '/coupons',
    label: 'Coupons',
    permission: PERMISSIONS.COUPONS_VIEW,
    icon: <Icon path="M9 12h6m-6 4h6m-9.75 4.5h13.5A2.25 2.25 0 0021 18.25V5.75A2.25 2.25 0 0018.75 3.5H5.25A2.25 2.25 0 003 5.75v12.5a2.25 2.25 0 002.25 2.25z" />,
  },
  {
    to: '/reports',
    label: 'Reports',
    permission: PERMISSIONS.REPORTS_VIEW,
    icon: <Icon path="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />,
  },
  {
    to: '/staff',
    label: 'Team',
    permission: PERMISSIONS.STAFF_VIEW,
    icon: <Icon path="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72M18 18.72V14.4c0-.6-.12-1.18-.34-1.7M18 18.72a9.09 9.09 0 01-12 0m12 0v-.01M6 18.72a9.094 9.094 0 01-3.741-.479 3 3 0 014.682-2.72M6 18.72V14.4c0-.6.12-1.18.34-1.7m0 0a5.002 5.002 0 019.32 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
  },
  {
    to: '/settings/payments',
    label: 'Payment methods',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: <Icon path="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />,
  },
  {
    to: '/billing',
    label: 'Plan & billing',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: <Icon path="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />,
  },
  {
    to: '/settings',
    label: 'Settings',
    permission: PERMISSIONS.SETTINGS_VIEW,
    icon: <Icon path="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />,
  },
];

export function DashboardLayout(): JSX.Element {
  const { session, logout, can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const visibleItems = NAV_ITEMS.filter((item) => !item.permission || can(item.permission));

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const storeSlug = session?.merchant?.slug;
  // Set only while a platform operator is looking at this store under a grant.
  const supportAccess = session?.supportAccess ?? null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile drawer backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-700 text-sm font-bold text-white">
            M
          </div>
          <span className="text-base font-semibold tracking-tight text-slate-900">MusleemPay</span>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-teal-50 text-teal-800'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                )
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-3">
          {storeSlug && (
            <a
              href={getStorefrontUrl(storeSlug)}
              target="_blank"
              rel="noreferrer"
              className="mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
            >
              <Icon path="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              View storefront
            </a>
          )}
          <div className="rounded-lg bg-slate-50 px-3 py-2.5">
            <p className="truncate text-sm font-medium text-slate-900">{session?.user.name}</p>
            <p className="truncate text-xs text-slate-500">
              {session?.role ? ROLE_LABELS[session.role] : 'Platform operator'}
            </p>
          </div>
        </div>
      </aside>

      <div className={clsx('lg:pl-64', supportAccess && 'pb-16')}>
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            aria-label="Open navigation"
          >
            <Icon path="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </button>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-900">
              {session?.merchant?.name ?? 'MusleemPay'}
            </p>
            {storeSlug && (
              <p className="truncate text-xs text-slate-500">
                {storeSlug}.{STOREFRONT_ROOT_DOMAIN}
              </p>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={() => void handleLogout()}>
            Sign out
          </Button>
        </header>

        {/* Not for an operator viewing the store: it is the merchant's notice. */}
        {!supportAccess && <PlanBanner />}

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      {supportAccess && <SupportAccessBanner access={supportAccess} />}
    </div>
  );
}
